/**
 * MOCK DATA & CANNED RESPONDER — VER-58 Assessment AI (5-prompt cap)
 *
 * This file is the ONLY place that generates AI responses in this surface.
 * No network calls, no AI SDK, no fetch/axios. Pure deterministic stubs.
 *
 * IMPORTANT — security note:
 * The 5-prompt cap enforced here is CLIENT-SIDE CONVENIENCE ONLY.
 * The real security boundary is the server-side quota on the API route that
 * will be built in VER-55 / VER-58 backend work. The server must reject a
 * 6th prompt with HTTP 429, regardless of what the client sends.
 * See VER-58-NOTES.md § "Server-side quota contract" for the expected shape.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum AI prompts allowed per assessment session. */
export const AI_PROMPT_LIMIT = 5;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MessageRole = "practitioner" | "ai";

export type AiMessage = {
    id: string;
    role: MessageRole;
    text: string;
    /** ISO timestamp */
    timestamp: string;
};

export type AiSessionState = {
    messages: AiMessage[];
    /** 0–AI_PROMPT_LIMIT inclusive */
    promptsUsed: number;
    /** true when promptsUsed === AI_PROMPT_LIMIT */
    limitReached: boolean;
};

// ---------------------------------------------------------------------------
// Canned response pool
// ---------------------------------------------------------------------------

/**
 * Deterministic responses rotated by (promptsUsed mod pool.length).
 * When the backend is wired, replace getMockAiResponse() with a real API call.
 */
const CANNED_RESPONSES: readonly string[] = [
    "Based on the assessment data, this participant appears to show signs of moderate wellbeing improvement since the programme began. Consider exploring how their sleep and social engagement have changed.",
    "The WEMWBS scores suggest a positive trend. It would be worth discussing what specific activities the participant found most meaningful during the sessions.",
    "Looking at the pre and post survey results, there is a notable shift in the optimism subscale. This is often linked to narrative engagement — the storytelling elements may be having an effect.",
    "The data pattern here is consistent with participants who benefit from structured reflection time. Would you like me to highlight the specific items where the largest score changes occurred?",
    "Across similar cases in this programme, scores in this range at this stage typically indicate readiness for a more independent pathway. The next conversation with the participant might focus on what support they feel they still need.",
];

// ---------------------------------------------------------------------------
// Mock responder
// ---------------------------------------------------------------------------

/**
 * Returns a canned AI response string for the given prompt index (0-based).
 * Simulates a ~600ms network delay via a Promise so the UI can show a loading
 * state — matching what the real endpoint will behave like.
 */
export function getMockAiResponse(promptIndex: number): Promise<string> {
    const text = CANNED_RESPONSES[promptIndex % CANNED_RESPONSES.length];
    return new Promise((resolve) => setTimeout(() => resolve(text), 650));
}

// ---------------------------------------------------------------------------
// Initial session state
// ---------------------------------------------------------------------------

export function createEmptySession(): AiSessionState {
    return {
        messages: [],
        promptsUsed: 0,
        limitReached: false,
    };
}
